import { Big } from 'big.js';
import {
  type Position,
  type PositionStatusReason,
  PositionStatusReasonPositionIncreased,
  PositionStatusReasonPositionInverted,
  PositionStatusReasonPositionOpened,
} from '@/api/spec';
import type { MarketData } from '@/common/MarketData';
import type { PNLEntry } from '@/feature/terminal_tables/position/PNLEntry.ts';
import { toBasePosition } from '@/feature/terminal_tables/position/positionUtils.ts';
import { mapApiPositionStatus } from '@/feature/terminal_tables/position/types';
import { amountToBig } from '@/utils/bigUtils.ts';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';

const ENTRY_FEE_REASONS = new Set<PositionStatusReason>([
  PositionStatusReasonPositionOpened,
  PositionStatusReasonPositionIncreased,
  PositionStatusReasonPositionInverted,
]);

export const mapPositionHistoryToPnL = (
  positions: Position[],
  markets: ReadonlyMap<number, MarketData>,
  flattenEvents: boolean
): PNLEntry[] => {
  const result: PNLEntry[] = [];

  const filteredPositions = flattenEvents
    ? positions
        .flatMap((p) => p.e)
        .filter((p) => p != null)
        .sort((pa, pb) => (pa.at.t ?? 0) - (pb.at.t ?? 0))
    : positions;

  for (const p of filteredPositions) {
    const market = markets.get(p.mkt);
    if (!market) continue;

    const base = toBasePosition(p, market);
    if (!base) continue;

    // Events that don't settle anything (opens, collateral changes) carry no exit price.
    const exitPrice = p.xp != null ? offsetNumberToNumber(p.xp, base.quoteDecimals) : undefined;
    const hasExit = exitPrice != null;

    const pricePnl = p.dpnl ? amountToBig(p.dpnl, market.quote.decimals) : new Big(0);
    const funding = p.fnd ? amountToBig(p.fnd, market.quote.decimals) : new Big(0);

    // Subtract the fee only on settlement rows: fees charged at open/increase roll into the
    // position's running fee total and are re-reported pro-rata by the settling events, so
    // subtracting them here too would double-count them across the history (dex-be #711). A settling
    // row's `fee` also includes its close fee (`cfee`), so it stays the full cost to net.
    // Non-settling rows still realize PnL — an increase materializes the accrued funding
    // (`fnd`), a collateral decrease beyond the deposit realizes the excess as `dpnl`.
    const totalPnl = hasExit ? pricePnl.plus(funding).minus(base.fee) : pricePnl.plus(funding);

    // Fee column shows what the event itself charged: `fee` on entry events, `cfee` on reducing ones
    // (their `fee` re-reports the entry share the open row already showed).
    // TODO(api): PositionInverted's fee/cfee split is unconfirmed; it shows `fee` until then.
    const collectedFee = ENTRY_FEE_REASONS.has(p.sr) ? base.fee : base.closeFee.gt(0) ? base.closeFee : undefined;
    // History events report `c` as the SIGNED collateral change (negative when the event
    // released collateral), so normalize before using it as margin / the ROE denominator.
    const collateral = base.collateral.abs();
    const roe = hasExit && collateral.gt(0) ? totalPnl.div(collateral) : new Big(0);

    // Decreases can lack an exit price; never fall back to the entry price.
    const notionalPrice = ENTRY_FEE_REASONS.has(p.sr) ? base.entryPrice : exitPrice;
    const notionalValue =
      base.size > 0 && notionalPrice != null && notionalPrice > 0
        ? new Big(base.size).times(notionalPrice).toNumber()
        : undefined;

    result.push({
      uniqueId: base.uniqueId,
      txHash: base.txHash,
      dateTimestamp: base.atTimestamp,
      marketId: base.marketId,
      positionId: base.positionId,
      assetCurrency: base.assetCurrency,
      assetDecimals: base.assetDecimals,
      quoteDecimals: base.quoteDecimals,
      initialLeverage: base.initialLeverage,
      side: base.side,
      entryPrice: base.entryPrice,
      exitPrice: exitPrice,
      size: base.size,
      notionalValue,
      margin: collateral.toNumber(),
      pnl: totalPnl.toNumber(),
      roe: roe.toNumber(),
      funding: funding.toNumber(),
      fee: base.fee.toNumber(),
      collectedFee: collectedFee?.toNumber(),
      insurancePayment: p.pay ? amountToBig(p.pay, market.quote.decimals).toNumber() : undefined,
      status: mapApiPositionStatus(p.st),
      statusReason: p.sr,
    });
  }

  return result;
};
